import validationStore from '../store/validationStore';
import * as glb from '../globals';

/**
 * maxLength alır: önce validationStore (API), yoksa globals.stringValidations
 */
function getMaxLength(modelName, fieldName) {
  let maxLength = validationStore.getMaxLength(modelName, fieldName);
  if (maxLength != null) return maxLength;
  const raw = glb.default?.stringValidations?.[modelName]?.[fieldName];
  return typeof raw === 'number' ? raw : (raw?.maxLength ?? null);
}

/**
 * Form validasyon helper'ı
 * Tüm SimpleItem'lar için otomatik maxLength ekler
 */

/**
 * SimpleItem editorOptions'ına maxLength ekler
 * @param {string} modelName - Model adı (örn: 'CARI_KART')
 * @param {string} fieldName - Field adı (örn: 'CAR_ADSOY')
 * @param {object} existingOptions - Mevcut editorOptions
 * @returns {object} - Güncellenmiş editorOptions
 */
export const addMaxLengthToEditorOptions = (modelName, fieldName, existingOptions = {}) => {
  const maxLength = getMaxLength(modelName, fieldName);
  
  if (!maxLength) {
    return existingOptions;
  }

  return {
    ...existingOptions,
    maxLength: maxLength
  };
};

/**
 * SimpleItem için tam props oluşturur
 * @param {string} modelName - Model adı
 * @param {string} fieldName - Field adı
 * @param {object} baseProps - Temel props
 * @returns {object} - Tam props
 */
export const createValidatedSimpleItemProps = (modelName, fieldName, baseProps = {}) => {
  const maxLength = getMaxLength(modelName, fieldName);
  const isRequired = validationStore.isRequired(modelName, fieldName);
  
  const editorOptions = addMaxLengthToEditorOptions(modelName, fieldName, baseProps.editorOptions || {});
  
  return {
    ...baseProps,
    editorOptions,
    // Required rule ekle
    children: [
      ...(isRequired ? [{
        type: 'required',
        message: `${fieldName} alanı zorunludur.`
      }] : []),
      ...(maxLength ? [{
        type: 'stringLength',
        max: maxLength,
        message: `${fieldName} maksimum ${maxLength} karakter olabilir.`
      }] : []),
      ...(baseProps.children || [])
    ]
  };
};

/**
 * TextArea için validasyonlu props
 */
export const createValidatedTextAreaProps = (modelName, fieldName, baseProps = {}) => {
  const maxLength = getMaxLength(modelName, fieldName);
  
  const editorOptions = {
    ...baseProps.editorOptions,
    ...(maxLength && { maxLength })
  };

  return {
    ...baseProps,
    editorOptions
  };
};

/**
 * TextBox için validasyonlu props
 */
export const createValidatedTextBoxProps = (modelName, fieldName, baseProps = {}) => {
  return createValidatedSimpleItemProps(modelName, fieldName, {
    editorType: 'dxTextBox',
    ...baseProps
  });
};

/**
 * SelectBox için validasyonlu props
 */
export const createValidatedSelectBoxProps = (modelName, fieldName, baseProps = {}) => {
  return createValidatedSimpleItemProps(modelName, fieldName, {
    editorType: 'dxSelectBox',
    ...baseProps
  });
};

/**
 * Form alanlarını otomatik validasyonlu hale getirir
 * Kullanım:
 * 
 * Eski:
 * <SimpleItem dataField='CAR_ADSOY' editorOptions={{ readOnly: true }}>
 * 
 * Yeni:
 * <SimpleItem {...createValidatedTextBoxProps('CARI_KART', 'CAR_ADSOY', {
 *   dataField: 'CAR_ADSOY',
 *   editorOptions: { readOnly: true }
 * })}>
 */
export const autoValidateFields = (modelName, fields) => {
  return fields.map(field => {
    const fieldName = field.dataField || field.name;
    const fieldType = field.editorType || 'dxTextBox';
    
    return {
      ...field,
      editorOptions: addMaxLengthToEditorOptions(modelName, fieldName, field.editorOptions || {})
    };
  });
};

/**
 * Grid column'ları için maxLength ekler
 * @param {string} modelName - Model adı
 * @param {string} fieldName - Field adı
 * @returns {object} - Grid column props
 */
export const addMaxLengthToGridColumn = (modelName, fieldName, baseProps = {}) => {
  const maxLength = getMaxLength(modelName, fieldName);
  
  if (!maxLength) {
    return baseProps;
  }

  return {
    ...baseProps,
    validationRules: [{
      type: 'stringLength',
      max: maxLength,
      message: `${fieldName} maksimum ${maxLength} karakter olabilir.`
    }],
    editorOptions: {
      ...baseProps.editorOptions,
      maxLength
    }
  };
};

/**
 * DataGrid için column props oluşturur
 */
export const createValidatedColumnProps = (modelName, fieldName, baseProps = {}) => {
  const maxLength = getMaxLength(modelName, fieldName);
  
  return {
    ...baseProps,
    ...(maxLength && {
      // DevExtreme DataGrid için maxLength kontrolü
      editCellTemplate: (container, options) => {
        const input = container.querySelector('.dx-texteditor-input');
        if (input) {
          input.maxLength = maxLength;
          input.addEventListener('input', (e) => {
            if (e.target.value.length > maxLength) {
              e.target.value = e.target.value.substring(0, maxLength);
            }
          });
        }
      }
    })
  };
};

export default {
  addMaxLengthToEditorOptions,
  createValidatedSimpleItemProps,
  createValidatedTextAreaProps,
  createValidatedTextBoxProps,
  createValidatedSelectBoxProps,
  autoValidateFields,
  addMaxLengthToGridColumn,
  createValidatedColumnProps
};
